'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import styles from '../styles/Login.module.css';
import { authApi } from '@/lib/api';

const BANNER_IMAGES = [
  '/images/login_asset.jpg',
  '/images/login_Inventory.jpg',
  '/images/login_smarten_ERP.jpg',
  '/images/registration_Slider_Smarten_ERP.jpg',
  '/images/registration_Slider_Inventory.jpg',
  '/images/registration_Slider_asset.jpg',
];

interface ForgotPasswordProps {
    onBackToLogin: () => void;
}

export function ForgotPassword({ onBackToLogin }: ForgotPasswordProps) {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [imgIndex, setImgIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setImgIndex(i => (i + 1) % BANNER_IMAGES.length), 5000);
        return () => clearInterval(id);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        try {
            const response = await authApi.forgotPassword({ email });
            setSuccess(response.message);
            setEmail('');
        } catch (err: any) {
            setError(err.message || 'Failed to send reset email');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.authCard}>
                {/* LEFT SIDE - FORGOT PASSWORD */}
                <div className={styles.leftPanel}>
                    <div className={styles.loginHeader}>
                        <Image
                            src="/images/logo.svg"
                            alt="Smarten"
                            width={140}
                            height={40}
                            className={styles.logo}
                        />
                        <h1 className={styles.welcomeTitle}>Forgot Password</h1>
                        <p className={styles.welcomeSubtitle}>
                            Enter your email to receive a password reset link
                        </p>
                    </div>

                    {error && (
                        <div className={styles.errorBanner}>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className={styles.successBanner}>
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.loginForm}>
                        <div className={styles.formGroup}>
                            <label htmlFor="email" className={styles.formLabel}>
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                className={styles.formInput}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                                disabled={isLoading || !!success}
                            />
                        </div>

                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading || !!success}
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                    </form>

                    <p className={styles.registerPrompt}>
                        Remember your password?{' '}
                        <button
                            type="button"
                            onClick={onBackToLogin}
                            className={styles.linkButton}
                            disabled={isLoading}
                        >
                            Back to login
                        </button>
                    </p>
                </div>

                {/* RIGHT SIDE - IMAGE PANEL */}
                <div className={styles.rightPanel}>
                    <Image key={imgIndex} src={BANNER_IMAGES[imgIndex]} alt="Image" fill className={styles.illustration} />
                </div>
            </div>
        </div>
    );
}
